import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { EditorView } from "@tiptap/pm/view";
import type { Node as PmNode } from "@tiptap/pm/model";

import { DocumentBrowserLayout } from "@/api/document/browser-layout";
import { A4_METRICS, A4_USABLE_HEIGHT, pageFlowHeight, paginate } from "@/api/document/paginate";

const paginationKey = new PluginKey<DecorationSet>("pagination");

export const PAGE_GAP_BAND = 22;

export const PAGE_STRIDE = A4_METRICS.pageHeight + PAGE_GAP_BAND;

export interface PaginationOptions {
	marginTop: number;
	marginBottom: number;
	marginLeft: number;
	usableHeight: number;
	onPagesChange?: (pages: string[][]) => void;
}

function posBeforeChild(doc: PmNode, index: number) {
	let pos = 0;

	for (let i = 0; i < index; i += 1) {
		pos += doc.child(i).nodeSize;
	}

	return pos;
}

function gapElement(input: { filler: number; last: boolean }, options: PaginationOptions) {
	const gap = document.createElement("div");
	gap.className = input.last ? "page-gap page-gap-last" : "page-gap";
	gap.contentEditable = "false";

	const spacer = document.createElement("div");
	spacer.style.height = `${Math.max(0, Math.round(input.filler)) + options.marginBottom}px`;
	gap.append(spacer);

	if (!input.last) {
		const band = document.createElement("div");
		band.className = "page-gap-band";
		gap.append(band);

		const top = document.createElement("div");
		top.style.height = `${options.marginTop}px`;
		gap.append(top);
	}

	return gap;
}

class PaginationView {
	private observer: ResizeObserver;
	private timer: ReturnType<typeof setTimeout> | null = null;
	private signature = "";
	private destroyed = false;

	constructor(
		private view: EditorView,
		private options: PaginationOptions,
	) {
		this.observer = new ResizeObserver(() => this.schedule());
		this.observer.observe(view.dom);

		const strip = view.dom.closest(".doc-a4-strip");

		if (strip !== null) {
			this.observer.observe(strip);
		}

		void document.fonts.ready.then(() => this.schedule());
	}

	update() {
		this.schedule();
	}

	destroy() {
		this.destroyed = true;
		this.observer.disconnect();

		if (this.timer !== null) {
			clearTimeout(this.timer);
		}
	}

	private schedule() {
		if (this.timer !== null) {
			clearTimeout(this.timer);
		}

		this.timer = setTimeout(() => {
			this.timer = null;
			this.compute();
		}, 120);
	}

	private compute() {
		if (this.destroyed) {
			return;
		}

		const strip = this.view.dom.closest(".doc-a4-strip");

		if (strip === null) {
			return;
		}

		const children = [...this.view.dom.children].filter(
			(element) => !element.classList.contains("page-gap"),
		);

		if (children.length !== this.view.state.doc.childCount) {
			return;
		}

		const { doc } = this.view.state;
		const items = DocumentBrowserLayout.measureChildren(this.view.dom, "page-gap").blocks;

		const pages = paginate(items, this.options.usableHeight);

		const blockIds = Array.from({ length: this.view.state.doc.childCount }, (_, index) => {
			const id = this.view.state.doc.child(index).attrs.id;

			return typeof id === "string" ? id : "";
		});

		const pageBlocks = pages.map((page) =>
			page.map((index) => blockIds[index]).filter((id) => id !== ""),
		);

		this.view.dom.dataset.pages = String(pageBlocks.length);
		this.view.dom.dataset.pageBlocks = JSON.stringify(pageBlocks);
		this.options.onPagesChange?.(pageBlocks);

		const fillers = pages.map((page) =>
			Math.max(0, this.options.usableHeight - pageFlowHeight(page.map((index) => items[index]))),
		);

		const signature = JSON.stringify({ pages, fillers: fillers.map(Math.round) });

		if (signature === this.signature) {
			return;
		}

		this.signature = signature;

		const decorations: Decoration[] = [];

		for (const [pageIndex, filler] of fillers.entries()) {
			const last = pageIndex === pages.length - 1;
			const spec = { filler, last };

			if (last) {
				decorations.push(
					Decoration.widget(doc.content.size, () => gapElement(spec, this.options), {
						side: 1,
						key: `gap-end-${signature}`,
					}),
				);

				continue;
			}

			const nextStart = pages[pageIndex + 1][0];

			decorations.push(
				Decoration.widget(posBeforeChild(doc, nextStart), () => gapElement(spec, this.options), {
					side: -1,
					key: `gap-${pageIndex}-${signature}`,
				}),
			);
		}

		this.view.dispatch(
			this.view.state.tr.setMeta(paginationKey, DecorationSet.create(doc, decorations)),
		);
	}
}

function paginationPlugin(options: PaginationOptions) {
	return new Plugin<DecorationSet>({
		key: paginationKey,
		state: {
			init: () => DecorationSet.empty,
			apply(tr, old) {
				const meta = tr.getMeta(paginationKey) as DecorationSet | undefined;

				if (meta !== undefined) {
					return meta;
				}

				return old.map(tr.mapping, tr.doc);
			},
		},
		props: {
			decorations(state: EditorState) {
				return paginationKey.getState(state);
			},
		},
		view: (view) => new PaginationView(view, options),
	});
}

export const PaginationExtension = Extension.create<PaginationOptions>({
	name: "pagination",

	addOptions() {
		return {
			marginTop: A4_METRICS.marginTop,
			marginBottom: A4_METRICS.marginBottom,
			marginLeft: A4_METRICS.marginLeft,
			usableHeight: A4_USABLE_HEIGHT,
		};
	},

	addProseMirrorPlugins() {
		return [paginationPlugin(this.options)];
	},
});
